// Step3Sign — canvas signature + finalize.
// Spec: docs/specs/location-tasks.md §8.6. PR M provides the canvas
// renderer; framework handles enable-state + completion display.

import React from "react";
import { Card, CardHeader } from "../../../liff/components/Mobile.js";
import { PenLine, CheckCircle2 } from "lucide-react";
import type { WizardData } from "./types.js";

interface Props {
  data: WizardData;
  renderCanvas?: (ctx: { data: WizardData; onComplete: () => void }) => React.ReactElement;
  onChanged: () => void;
}

export default function Step3Sign({ data, renderCanvas, onChanged }: Props): React.ReactElement {
  const t = data.task;
  const s = data.submission;
  if (t.requires_signature !== 1) {
    return (
      <Card>
        <p className="text-sm text-text-secondary">此任務未要求簽名。</p>
      </Card>
    );
  }
  const done = !!s.signature_r2_key;
  return (
    // select-none on the whole signature card: the operator's finger
    // is going to drag across this region in earnest, and any text
    // (instruction, hint, even the card heading) under that motion
    // mustn't trigger system text selection or the iOS callout menu.
    // StepCanvasSignature applies the same rule to its own subtree so
    // the canvas area is doubly protected. Inputs in other steps stay
    // selectable because Step 2/1 don't sit inside this card.
    <Card className="select-none [-webkit-touch-callout:none]">
      <CardHeader title="簽名" hint={done ? "已簽名" : "Canvas"} />
      <div className={`flex items-start gap-2 mb-3 ${done ? "opacity-70" : ""}`}>
        <PenLine className={`h-4 w-4 mt-0.5 ${done ? "text-success" : "text-text-secondary"}`} />
        <p className="text-xs text-text-secondary leading-relaxed">
          請於下方畫布以手指或觸控筆簽名，完成後送出即提交此任務。
        </p>
      </div>
      {done ? (
        <p className="text-xs text-success inline-flex items-center gap-1">
          <CheckCircle2 className="h-3.5 w-3.5" />簽名已上傳
        </p>
      ) : renderCanvas ? renderCanvas({ data, onComplete: onChanged }) : (
        <button disabled className="block w-full h-11 rounded-xl bg-bg-raised text-text-muted text-sm font-medium">
          此簽名流程尚未上線
        </button>
      )}
    </Card>
  );
}
